import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatTooltip } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { Subject, timer } from 'rxjs';
import { debounce, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { Alarm } from '../../../class/Alarm';
import { Device } from '../../../class/Device';
import { GeoDirect } from '../../../class/Geo';
import { Maintenance } from '../../../class/Maintenance';
import { Driver, Landmark, Unit, User } from '../../../database/models';
import { AuthService } from '../../../services/auth.service';
import { DeviceScannerService } from '../../../services/device-scanner.service';
import { GeoDirectService } from '../../../services/geodirect.service';
import { GlobalSearchService } from '../../../services/global-search.service';
import { RoutingService } from '../../../services/routing.service';
import HasPermissionDirective from '../../../utils/directives/has-permission.directive';
import { USERS } from '../../administration-tab/admin-routing.constants';
import { DEVICE_HISTORY, DEVICES, DEVICES_UNIT_DEVICE } from '../../devices/devices-routing.constants';
import { BUILD } from '../../maintenances-tab/maintenances-routing.constants';
import { ALARMS } from '../../management-tab/alarms-sub-tab/alarms-routing.constants';
import {
  LANDMARK_HISTORY,
  LANDMARKS_CATEGORY_LANDMARK,
} from '../../management-tab/landmarks-sub-tab/landmarks-routing.constants';
import { DRIVER_HISTORY, DRIVERS } from '../../mobility-tab/drivers-sub-tab/drivers-routing.constants';
import { NoResultFoundComponent } from '../no-result-found/no-result-found.component';
import { ResultListComponent } from '../result-list/result-list.component';

// Panel class to pass when opening the dialog: see styles.scss.
export const SEARCH_DIALOG_PANEL_CLASS = 'global-search-dialog-panel';

export type searchResult = Landmark | Device | Driver | Alarm | User | Maintenance | Unit | GeoDirect;
export interface Folder {
  id: string;
  label: string;
  icon: string;
  searchResults: searchResult[];
  resultElements: {
    name: string;
    subName?: string;
  };
  actions: {
    icon: string;
    path?: string | ((unitId?: number, id?: number) => string);
    tooltipText: string;
  }[];
  permissionTab: 'device' | 'poi' | 'alarm' | 'driver' | 'administration' | 'maintenance';
}

@Component({
  selector: 'search-dialog',
  imports: [
    ResultListComponent,
    MatFormFieldModule,
    MatIconModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    TranslatePipe,
    MatInputModule,
    MatListModule,
    CommonModule,
    HasPermissionDirective,
    NoResultFoundComponent,
    MatIconButton,
    MatSlideToggle,
    MatTooltip,
  ],
  templateUrl: './search-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./search-dialog.component.scss'],
})
export class SearchDialogComponent implements OnDestroy, OnInit {
  // SERVICES
  private dialogRef = inject(MatDialogRef<SearchDialogComponent>);
  private _authService = inject(AuthService);
  private geodirectService = inject(GeoDirectService);
  private globalSearchService = inject(GlobalSearchService);
  public routingService = inject(RoutingService);
  public deviceScannerService = inject(DeviceScannerService);
  private _router = inject(Router);

  private _searchInputElement = viewChild<ElementRef<HTMLInputElement>>('searchInputElement');

  // VARIABLES
  public searchInput = new FormControl('');
  private destroyComponent$ = new Subject<void>();
  public $isMobile = this.routingService.$isMobile;

  // SIGNALS
  public $displayResults = signal<boolean>(false);
  public $selectedEntityId = signal<string | null>(null);
  public $disabledEntities = signal({ addresses: true });

  public $addressesSearch = signal<GeoDirect[]>([]);
  public $devicesSearch = signal<Device[]>([]);
  public $tagsSearch = signal<Device[]>([]);
  public $poisSearch = signal<Landmark[]>([]);
  public $driversSearch = signal<Driver[]>([]);
  public $alarmsSearch = signal<Alarm[]>([]);
  public $usersSearch = signal<User[]>([]);
  public $maintenancesSearch = signal<Maintenance[]>([]);
  public $servicesSearch = signal<Unit[]>([]);

  public $folders = computed<Folder[]>(() => [
    {
      id: 'addresses',
      label: 'GLOBALSEARCH.ADDRESSES',
      icon: 'near_me',
      searchResults: this.$addressesSearch(),
      resultElements: {
        name: 'formattedAddress',
      },
      actions: [{ icon: 'map', tooltipText: 'MAP.SEEMAP' }],
      permissionTab: 'device',
    },
    {
      id: 'devices',
      label: 'COMMON.DEVICES',
      icon: 'local_shipping',
      searchResults: this.$devicesSearch(),
      resultElements: { name: 'registration', subName: 'summary' },
      actions: [
        { icon: 'arrow_forward', path: DEVICES_UNIT_DEVICE, tooltipText: 'GLOBALSEARCH.DETAILS' },
        { icon: 'directions', path: DEVICE_HISTORY, tooltipText: 'GLOBALSEARCH.HISTORY' },
      ],
      permissionTab: 'device',
    },
    {
      id: 'beacons',
      label: 'COMMON.BEACONS',
      icon: 'mode_standby',
      searchResults: this.$tagsSearch(),
      resultElements: { name: 'registration', subName: 'summary' },
      actions: [
        { icon: 'arrow_forward', path: DEVICES_UNIT_DEVICE, tooltipText: 'GLOBALSEARCH.DETAILS' },
        { icon: 'directions', path: DEVICE_HISTORY, tooltipText: 'GLOBALSEARCH.HISTORY' },
      ],
      permissionTab: 'device',
    },
    {
      id: 'pois',
      label: 'TAB.POI',
      icon: 'location_on',
      searchResults: this.$poisSearch(),
      resultElements: { name: 'label', subName: 'address1' },
      actions: [
        { icon: 'arrow_forward', path: LANDMARKS_CATEGORY_LANDMARK, tooltipText: 'GLOBALSEARCH.DETAILS' },
        { icon: 'directions', path: LANDMARK_HISTORY, tooltipText: 'GLOBALSEARCH.HISTORY' },
      ],
      permissionTab: 'poi',
    },
    {
      id: 'alarms',
      label: 'TAB.ALARM',
      icon: 'alarm',
      searchResults: this.$alarmsSearch(),
      resultElements: { name: 'name', subName: 'typeText' },
      actions: [{ icon: 'arrow_forward', path: ALARMS, tooltipText: 'GLOBALSEARCH.DETAILS' }],
      permissionTab: 'alarm',
    },
    {
      id: 'units',
      label: 'COMMON.SERVICES',
      icon: 'folder',
      searchResults: this.$servicesSearch(),
      resultElements: { name: 'label' },
      actions: [{ icon: 'arrow_forward', path: DEVICES, tooltipText: 'GLOBALSEARCH.DETAILS' }],
      permissionTab: 'device',
    },
    {
      id: 'maintenances',
      label: 'TAB.MAINTENANCE',
      icon: 'build',
      searchResults: this.$maintenancesSearch(),
      resultElements: { name: 'motif', subName: 'registration' },
      actions: [{ icon: 'arrow_forward', path: BUILD, tooltipText: 'GLOBALSEARCH.DETAILS' }],
      permissionTab: 'maintenance',
    },
    {
      id: 'drivers',
      label: 'TAB.DRIVER',
      icon: 'record_voice_over',
      searchResults: this.$driversSearch(),
      resultElements: { name: 'label' },
      actions: [
        { icon: 'arrow_forward', path: DRIVERS, tooltipText: 'GLOBALSEARCH.DETAILS' },
        { icon: 'directions', path: DRIVER_HISTORY, tooltipText: 'GLOBALSEARCH.HISTORY' },
      ],
      permissionTab: 'driver',
    },
    {
      id: 'users',
      label: 'TAB.USERS',
      icon: 'person',
      searchResults: this.$usersSearch(),
      resultElements: { name: 'fullName' },
      actions: [{ icon: 'arrow_forward', path: USERS, tooltipText: 'GLOBALSEARCH.DETAILS' }],
      permissionTab: 'administration',
    },
  ]);

  public $selectedEntity = computed(() => this.$folders().find(({ id }) => id === this.$selectedEntityId()));

  public $totalCount = computed(() => {
    let count = 0;
    this.$folders().forEach(folder => (count += folder.searchResults.length));
    return count;
  });

  ngOnInit() {
    this.searchInput.valueChanges
      .pipe(
        map(v => v ?? ''),
        debounce(query => {
          // moins de 3 caractères => on ralentit
          return timer(query.length < 3 ? 800 : 500);
        }),
        distinctUntilChanged(),
        takeUntil(this.destroyComponent$)
      )
      .subscribe(query => {
        if (query.length === 0) {
          this.clearSearch();
          return;
        }

        this.searchAll(query);
      });
  }

  public onSelectResult() {
    this.dialogRef.close();
  }

  public onEntityClick(id: string | null) {
    this.$selectedEntityId.set(id);
  }

  // TODO: remove this function when all entities are migrated with index db
  private searchAll(query: string) {
    const results = this.globalSearchService.search(query);

    this.$devicesSearch.set(results.devices || []);
    this.$tagsSearch.set(results.beacons || []);
    this.$poisSearch.set(results.landmarks || []);
    this.$driversSearch.set(results.drivers || []);
    this.$alarmsSearch.set(results.alarms || []);
    this.$usersSearch.set(results.users || []);
    this.$maintenancesSearch.set(results.maintenances || []);
    this.$servicesSearch.set(results.units || []);

    this.$displayResults.set(true);

    // addresses
    if (!this.$disabledEntities()['addresses']) {
      this.findAddress(query);
    }
  }

  public findAddress(query: string) {
    this.geodirectService
      .Get('geodirect', {
        token: this._authService.$token(),
        search_string: query,
      })
      .subscribe(
        results => {
          this.$addressesSearch.set(results);
        },
        err => {
          console.log(err);
        }
      );
  }

  public clearSearch() {
    this.$displayResults.set(false);
    this.$devicesSearch.set([]);
    this.$tagsSearch.set([]);
    this.$poisSearch.set([]);
    this.$driversSearch.set([]);
    this.$alarmsSearch.set([]);
    this.$usersSearch.set([]);
    this.$maintenancesSearch.set([]);
    this.$servicesSearch.set([]);
    this.$addressesSearch.set([]);
  }

  public openScanner() {
    this.deviceScannerService
      .open()
      .pipe(takeUntil(this.destroyComponent$))
      .subscribe(result => {
        if (!result) return;

        if ('device' in result) {
          const { uid_parent, uid } = result.device;
          this._router.navigate([DEVICES_UNIT_DEVICE(uid_parent, uid)], { queryParamsHandling: 'preserve' });
          this.dialogRef.close();
        } else if ('code' in result) {
          this.searchInput.setValue(result.code);
        } else {
          this._searchInputElement()?.nativeElement.focus();
        }
      });
  }

  public toggleSearchAddress() {
    this.$disabledEntities.update(value => ({ ...value, addresses: !this.$disabledEntities()['addresses'] }));

    if (this.$disabledEntities()['addresses']) {
      this.$addressesSearch.set([]);
    } else if (this.searchInput.value) this.findAddress(this.searchInput.value);

    // If the selection is set to addresses and the option is disabled, the user will not be able to view the addresses.
    if (this.$disabledEntities()['addresses'] && this.$selectedEntityId() === 'addresses')
      this.$selectedEntityId.set(null);
  }

  ngOnDestroy() {
    this.clearSearch();
    this.destroyComponent$.next();
    this.destroyComponent$.complete();
  }
}
